import React from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { priceFormatter } from '@noths/polaris-client-utils';
import { Icon } from '@noths/polaris-design-system-core-components';
import type { CurrencyCode } from '@noths/polaris-design-system-ooux-interfaces';

import type { Discount } from './getDiscount';
import * as styles from './ProductCardPrice.styles';

/**
 * Which colour the price and saving take. Undiscounted prices stay in the
 * default content colour; a discount is either good news about this product
 * (offer) or part of a sale event (sale).
 */
type PriceEmphasis = 'none' | 'offer' | 'sale';

const getEmphasis = (isDiscounted: boolean, promoteAsSale: boolean): PriceEmphasis => {
  if (!isDiscounted) {
    return 'none';
  }

  return promoteAsSale ? 'sale' : 'offer';
};

interface ProductCardPriceProps {
  currencyCode: CurrencyCode;
  discount: Discount | undefined;
  price: number;
  promoteAsSale: boolean;
  /** The rating slot, laid out at the far end of the price row. */
  rating?: React.ReactNode;
}

/**
 * The price row and, for a discounted product, the saving beneath it.
 *
 * The saving is the percentage alone. The design also shows the money saved
 * beside it ("-17% · £5 off"), but the percentage on its own is agreed and the
 * design will be updated to match, so don't add the amount back.
 *
 * As an *offer* the original price comes first and the price the customer pays
 * is positive-coloured. As a *sale* the price the customer pays leads, marked
 * with a sale tag and in the negative colour, which is the language the site
 * uses for a sale event. The data is identical; only the emphasis changes.
 */
export const ProductCardPrice = ({
  currencyCode,
  discount,
  price,
  promoteAsSale,
  rating,
}: ProductCardPriceProps) => {
  const emphasis = getEmphasis(Boolean(discount), promoteAsSale);
  const isSale = emphasis === 'sale';

  const currentPrice = (
    <span css={styles.price} data-testid="product-card-price">
      {priceFormatter(currencyCode, price)}
    </span>
  );

  const originalPrice = discount && (
    <>
      <span css={visuallyHidden}>Was</span>
      <s css={styles.originalPrice} data-testid="product-card-original-price">
        {priceFormatter(currencyCode, discount.originalPrice)}
      </s>
    </>
  );

  return (
    <>
      <p css={styles.priceRow} data-emphasis={emphasis}>
        <span css={styles.prices}>
          {isSale && (
            <span css={styles.saleIcon}>
              <Icon aria-hidden data-icon="sale" name="sale" size="sm" />
            </span>
          )}
          {isSale ? (
            <>
              {currentPrice}
              {originalPrice}
            </>
          ) : (
            <>
              {originalPrice}
              {currentPrice}
            </>
          )}
        </span>
        {rating}
      </p>
      {discount && (
        <p css={styles.discount} data-emphasis={emphasis} data-testid="product-card-discount">
          {`-${discount.percentage}%`}
        </p>
      )}
    </>
  );
};

ProductCardPrice.displayName = 'ProductCardPrice';
